import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { Button, Card, CardBody, Col, Container, Input, Row } from "reactstrap";
import { DEFAULT_PAGE_SIZE, pascalToCamel } from "helpers/GeneralHelper";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import CommonTableForCandidate from "components/Common/CandidateTableContainer";
import LegacyFilterToolbar, {
  LEGACY_FILTER_INPUT_CLASS,
} from "components/Common/LegacyFilterToolbar";
import { AsyncClientSelect, AsyncUserSelect } from "components/Common/AsyncSearchSelects";
import {
  applyClientAsyncFilterOptionToFormState,
  applyUserAsyncFilterOptionToFormState,
  buildClientAsyncFilterInitialOption,
  buildUserAsyncFilterInitialOption,
  getClientAsyncFilterLabelFieldKey,
  getUserAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
} from "helpers/metadataFilterHelpers";
import {
  getModifyClosureMetadata,
  submitModifyClosureFilterData,
  deleteCandidateClosure,
} from "model/ModifyClosureModel";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import ModifyClosureEditModal from "pages/Candidates/ModifyClosure/modify-closure-edit-modal";
import { useCandidateTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

const defaultTableColumns = [
  { Header: "ID", accessor: "id" },
  { Header: "Candidate", accessor: "candidateFullName" },
  { Header: "Client", accessor: "clientName" },
  { Header: "Close By", accessor: "closeByUserName" },
  { Header: "Close Date", accessor: "closeDateTime" },
  { Header: "Type", accessor: "closeType" },
];

/** Normalize `Candidate/closure/filter` record shape for table + edit modal. */
const normalizeClosureRecord = (item = {}) => {
  const id = item?.id ?? item?.closureId ?? item?._id ?? "";
  const closeByUserId = String(
    item.closeByUserId ?? item.CloseByUserId ?? item.userId ?? item.UserId ?? ""
  ).trim();
  const closeByUserName = String(
    item.closeByUserName ?? item.CloseByUserName ?? ""
  ).trim();
  const closeDateTime = item.closeDateTime ?? item.CloseDateTime ?? "";
  const candidateFullName = item.candidateFullName ?? item.CandidateFullName ?? "";
  const clientName = item.clientName ?? item.ClientName ?? "";

  return {
    ...item,
    id,
    closeByUserId,
    closeByUserName,
    closeDateTime,
    candidateFullName,
    candidateId: item.candidateId ?? item.CandidateId ?? item.candidate_id,
    clientId: item.clientId ?? item.ClientId ?? item.client_id,
    clientName,
    closeType: item.closeType ?? item.CloseType ?? item.type,
    candidate: candidateFullName || item.candidate,
    client: clientName || item.client,
    closeBy: closeByUserName || item.closeBy,
    closeDate: closeDateTime || item.closeDate,
  };
};

const strOrNull = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s === "" ? null : s;
};

const normalizeClosureFilterPayload = (raw = {}) => {
  const out = { ...raw };
  ["nameContains", "emailContains", "clientId", "userId", "fromDate", "toDate"].forEach((key) => {
    if (key in out) out[key] = strOrNull(out[key]);
  });
  Object.keys(out).forEach((key) => {
    if (isAsyncFilterLabelFieldKey(key)) delete out[key];
  });
  return out;
};

const getClosureRowId = (row = {}) =>
  String(row?.id ?? row?.closureId ?? row?._id ?? "").trim();

const mapClosureRecordsToRows = (list = []) =>
  (Array.isArray(list) ? list : []).map((item) => normalizeClosureRecord(item));

const noop = () => {};

const ModifyClosure = () => {
  const { permissionData } = usePermissionContext() || {};
  const { fetchMetadataSchema } = useCommonFilterOptions();
  const { pageSize: settingsPageSize, settingsReady } =
    useCandidateTableSettingsPageSize("ModifyClosure");
  const initialFilterFetchedRef = useRef(false);
  const [metadataBootstrapDone, setMetadataBootstrapDone] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetaData] = useState([]);
  const [filterForm, setFilterForm] = useState({
    search: "",
    clientId: "",
    [getClientAsyncFilterLabelFieldKey()]: "",
    userId: "",
    [getUserAsyncFilterLabelFieldKey()]: "",
    fromDate: "",
    toDate: "",
  });

  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    nameContains: null,
    emailContains: null,
    clientId: null,
    userId: null,
    fromDate: null,
    toDate: null,
  });

  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });

  const [closureEditOpen, setClosureEditOpen] = useState(false);
  const [closureEditContext, setClosureEditContext] = useState(null);

  const modifyClosureActionPermissions = useMemo(
    () => ({
      canEdit: Boolean(permissionData?.CandidateModifyClosureAccess),
      canDelete: Boolean(permissionData?.CandidateModifyClosureAccess),
    }),
    [permissionData]
  );

  const hasAnyModifyClosureRowAction = useMemo(
    () =>
      modifyClosureActionPermissions.canEdit ||
      modifyClosureActionPermissions.canDelete,
    [modifyClosureActionPermissions]
  );

  const getClosureList = useCallback(
    async (payloadOverrides = {}, options = {}) => {
      const { onlyPagination = false } = options;
      setLoading(true);
      try {
        const payload = onlyPagination
          ? {
              pageNumber: Number(payloadOverrides.pageNumber) || 1,
              pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
            }
          : normalizeClosureFilterPayload({
              pageNumber: requestPayload.pageNumber,
              pageSize: requestPayload.pageSize,
              nameContains: requestPayload.nameContains,
              emailContains: requestPayload.emailContains,
              clientId: requestPayload.clientId,
              userId: requestPayload.userId,
              fromDate: requestPayload.fromDate,
              toDate: requestPayload.toDate,
              ...payloadOverrides,
            });

        const response = await submitModifyClosureFilterData(payload);
        const responseData = response?.data ?? response ?? {};
        const listData =
          responseData?.records ??
          responseData?.data ??
          (Array.isArray(responseData) ? responseData : []);

        const records = Array.isArray(listData) ? listData : [];
        setRows(mapClosureRecordsToRows(records));

        if (response == null || response?.success !== false) {
          setPaginationMeta({
            pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
            pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
            totalPages: Number(responseData?.totalPages) || 0,
            totalRecords: Number(responseData?.totalRecords) || records.length,
          });
          if (onlyPagination) {
            setRequestPayload({
              pageNumber: payload.pageNumber,
              pageSize: payload.pageSize,
              nameContains: null,
              emailContains: null,
              clientId: null,
              userId: null,
              fromDate: null,
              toDate: null,
            });
            setFilterForm({
              search: "",
              clientId: "",
              [getClientAsyncFilterLabelFieldKey()]: "",
              userId: "",
              [getUserAsyncFilterLabelFieldKey()]: "",
              fromDate: "",
              toDate: "",
            });
          } else {
            setRequestPayload(normalizeClosureFilterPayload(payload));
          }
        } else {
          setRows([]);
          if (response?.message) {
            toast.error(response.message);
          }
        }
      } catch (err) {
        toast.error(err?.message || "Unable to load closure list");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [requestPayload]
  );

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:modifyClosure", () =>
          getModifyClosureMetadata()
        );
        const meta = metaRes?.data ?? metaRes ?? {};

        const cols = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => ({
              Header: col?.title,
              accessor: pascalToCamel(col?.field),
              type: col?.type,
            }))
          : defaultTableColumns;
        setTableMetaData(cols);
      } catch (e) {
        toast.error(e?.message || "Unable to load closure metadata");
        setTableMetaData(defaultTableColumns);
      }

      setMetadataBootstrapDone(true);
    };
    bootstrap();
  }, []);

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
    if (!settingsReady || !metadataBootstrapDone || initialFilterFetchedRef.current) return;
    initialFilterFetchedRef.current = true;
    const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
    setRequestPayload((previous) => ({ ...previous, pageSize }));
    setPaginationMeta((previous) => ({ ...previous, pageSize }));
    void getClosureList({ pageNumber: 1, pageSize }, { onlyPagination: true });
  }, [settingsReady, settingsPageSize, metadataBootstrapDone, getClosureList]);

  const buildPayloadFromUpperForm = (form = {}) => {
    const search = String(form?.search || "").trim();
    return normalizeClosureFilterPayload({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      nameContains: strOrNull(search),
      emailContains: strOrNull(search),
      clientId: strOrNull(form?.clientId),
      userId: strOrNull(form?.userId),
      fromDate: strOrNull(form?.fromDate),
      toDate: strOrNull(form?.toDate),
    });
  };

  const handleUpperSearch = () => {
    getClosureList(buildPayloadFromUpperForm(filterForm));
  };

  const handlePageChange = async (page) => {
    await getClosureList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    await getClosureList({ pageNumber: 1, pageSize: rows });
  };

  /** Merge API/form updates into one visible row without re-running the filter POST. */
  const applyClosureRowPatch = useCallback((closureId, partialRawUpdate = {}) => {
    const targetId = String(closureId ?? "").trim();
    if (!targetId || !partialRawUpdate || typeof partialRawUpdate !== "object") return;
    setRows((previousRows) => {
      let rowMatched = false;
      const nextRows = previousRows.map((tableRow) => {
        if (getClosureRowId(tableRow) !== targetId) return tableRow;
        rowMatched = true;
        const mergedRawRecord = {
          ...tableRow,
          ...partialRawUpdate,
          id: partialRawUpdate?.id ?? tableRow?.id ?? targetId,
        };
        const [mappedTableRow] = mapClosureRecordsToRows([mergedRawRecord]);
        return mappedTableRow;
      });
      return rowMatched ? nextRows : previousRows;
    });
  }, []);

  const removeClosureRowFromList = useCallback((closureId = "") => {
    const targetId = String(closureId ?? "").trim();
    if (!targetId) return;
    setRows((previousRows) => {
      const nextRows = previousRows.filter(
        (tableRow) => getClosureRowId(tableRow) !== targetId
      );
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount > 0) {
        setPaginationMeta((previousPagination) => ({
          ...previousPagination,
          totalRecords: Math.max(
            0,
            (Number(previousPagination?.totalRecords) || 0) - removedCount
          ),
        }));
      }
      return nextRows;
    });
  }, []);

  const handleClosureEditSuccess = useCallback(
    (updatedRawClosure) => {
      if (!updatedRawClosure || typeof updatedRawClosure !== "object") return;
      const targetId = getClosureRowId(updatedRawClosure);
      if (!targetId) return;
      applyClosureRowPatch(targetId, updatedRawClosure);
    },
    [applyClosureRowPatch]
  );

  const getCandidateIdFromRow = (row = {}) =>
    String(
      row?.candidateId ??
        row?.CandidateId ??
        row?.candidate_id ??
        row?.candidate?.id ??
        row?.candidate?._id ??
        ""
    ).trim();

  const pickClosureUserId = (row = {}) =>
    String(
      row?.closeByUserId ??
        row?.CloseByUserId ??
        row?.userId ??
        row?.UserId ??
        row?.closedById ??
        row?.ClosedById ??
        row?.recruiterUserId ??
        row?.RecruiterUserId ??
        ""
    ).trim();

  const pickClosureUserLabel = (row = {}) =>
    String(
      row?.closeByUserName ??
        row?.CloseByUserName ??
        row?.closeBy ??
        row?.CloseBy ??
        row?.closedBy ??
        row?.ClosedBy ??
        row?.userName ??
        row?.UserName ??
        row?.recruiterName ??
        row?.RecruiterName ??
        row?.recruiter ??
        ""
    ).trim();

  const pickClosureClosedDateRaw = (row = {}) =>
    row?.closeDateTime ??
    row?.CloseDateTime ??
    row?.closeDate ??
    row?.CloseDate ??
    row?.closedDate ??
    row?.ClosedDate ??
    row?.close_date ??
    row?.closed_date ??
    "";

  const handleEdit = (item) => {
    const row = item && typeof item === "object" ? item : {};
    setClosureEditContext({
      id: item?.id,
      initialUserId: pickClosureUserId(row),
      initialUserLabel: pickClosureUserLabel(row),
      initialClosedDateRaw: pickClosureClosedDateRaw(row) || undefined,
    });
    setClosureEditOpen(true);
  };

  const closeClosureEditModal = () => {
    setClosureEditOpen(false);
    setClosureEditContext(null);
  };

  const handleDelete = async (item) => {
    const id = getClosureRowId(item);
    if (!id) {
      toast.error("Closure record ID not found");
      return;
    }
    const result = await Swal.fire({
      title: "Delete closure",
      text: "Remove this closure record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await deleteCandidateClosure(id);
      if (res?.success === false) {
        toast.error(res?.message || "Unable to delete");
      } else {
        toast.success(res?.message || "Closure deleted successfully");
        removeClosureRowFromList(id);
      }
    } catch (e) {
      toast.error(e?.message || "Unable to delete");
    }
  };

  return (
    <div className="page-content modify-closure-page">
      <Container fluid>
        <Card>
          <CardBody>
            <h5 className="fw-bold mb-3">Modify Closure</h5>
            <hr />

            <LegacyFilterToolbar>
            <Row className="metadata-filter-toolbar__row g-2 g-md-3 align-items-end">
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--text">
                <span className="manage-user-filter-label">Search</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="text"
                  name="search"
                  placeholder="Search by Email/Company"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.search}
                  onChange={(e) => {
                    const nextValue = e?.target?.value || "";
                    setFilterForm((prev) => ({ ...prev, search: nextValue }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      handleUpperSearch();
                    }
                  }}
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">Client</span>
                <div className="metadata-filter-field__control">
                <AsyncClientSelect
                  key={`modify-closure-client-${filterForm.clientId}`}
                  pageName="ModifyClosure"
                  value={filterForm.clientId}
                  initialOption={buildClientAsyncFilterInitialOption(
                    filterForm.clientId,
                    filterForm[getClientAsyncFilterLabelFieldKey()]
                  )}
                  onChange={(option) =>
                    setFilterForm((prev) => applyClientAsyncFilterOptionToFormState(prev, option))
                  }
                  placeholder="Select Client"
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--autocomplete">
                <span className="manage-user-filter-label">User</span>
                <div className="metadata-filter-field__control">
                <AsyncUserSelect
                  key={`modify-closure-user-${filterForm.userId}`}
                  pageName="ModifyClosure"
                  value={filterForm.userId}
                  initialOption={buildUserAsyncFilterInitialOption(
                    filterForm.userId,
                    filterForm[getUserAsyncFilterLabelFieldKey()]
                  )}
                  onChange={(option) =>
                    setFilterForm((prev) => applyUserAsyncFilterOptionToFormState(prev, option))
                  }
                  placeholder="Select User"
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">From date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="fromDate"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.fromDate}
                  onChange={(e) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      fromDate: e?.target?.value || "",
                    }))
                  }
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--date">
                <span className="manage-user-filter-label">To date</span>
                <div className="metadata-filter-field__control">
                <Input
                  type="date"
                  name="toDate"
                  className={LEGACY_FILTER_INPUT_CLASS}
                  value={filterForm.toDate}
                  onChange={(e) =>
                    setFilterForm((prev) => ({
                      ...prev,
                      toDate: e?.target?.value || "",
                    }))
                  }
                />
                </div>
              </Col>
              <Col xs="12" md="2" className="metadata-filter-field metadata-filter-field--actions">
                <span className="manage-user-filter-label" aria-hidden="true">&nbsp;</span>
                <div className="metadata-filter-toolbar__actions">
                <Button color="primary" className="w-100 manage-user-search-btn" type="button" onClick={handleUpperSearch}>
                  <i className="fa fa-search me-1" />
                  Search
                </Button>
                </div>
              </Col>
            </Row>
            </LegacyFilterToolbar>

            {loading ? (
              <Loaders />
            ) : tableMetaData?.length ? (
              <CommonTableForCandidate
                columns={tableMetaData}
                data={rows}
                isGlobalFilter={false}
                isSort
                isActionRequired={hasAnyModifyClosureRowAction}
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                pageName="ModifyClosure"
                isCheckboxLeftSide={false}
                isPagination
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={noop}
                setCommonResetFilter={noop}
                allCommonFilter={false}
                onEdit={
                  modifyClosureActionPermissions.canEdit ? handleEdit : undefined
                }
                onDelete={
                  modifyClosureActionPermissions.canDelete ? handleDelete : undefined
                }
              />
            ) : (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            )}
          </CardBody>
        </Card>

        <ModifyClosureEditModal
          isOpen={closureEditOpen}
          onClose={closeClosureEditModal}
          id={closureEditContext?.id}
          initialUserId={closureEditContext?.initialUserId}
          initialUserLabel={closureEditContext?.initialUserLabel}
          initialClosedDateRaw={closureEditContext?.initialClosedDateRaw}
          onSuccess={handleClosureEditSuccess}
        />
      </Container>
    </div>
  );
};

ModifyClosure.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ModifyClosure);
